/**
 * Schemas de entrada de `/integracoes` (`03-endpoints-draft.md §12`).
 *
 * Decisão A7: validação por Zod, no servidor, com `.strict()` — campo
 * desconhecido no corpo é recusado, e filtro desconhecido na query também
 * (§6.5: "filtro desconhecido é 422, não ignorado").
 *
 * O corpo do **webhook** não tem schema aqui, e isso é deliberado: RNF-90 diz que
 * nenhum lead pode ser perdido, e um schema estrito no webhook transformaria
 * "a Meta acrescentou um campo" em payload descartado. O webhook aceita qualquer
 * JSON, grava bruto e deixa a interpretação para o processador — que valida o que
 * precisa e registra o resto em `payload_externo`.
 */
import { z } from 'zod'

import { zDataFiltro, zIdCatalogo, zUuid } from '../comum/campos'
import { schemaCursor, schemaLimite, schemaListaDeEnum } from '../comum/paginacao'
import {
  STATUS_PROCESSAMENTO,
  TIPO_DISTRIBUICAO_PADRAO,
  zOrigemEvento,
  zTipoDistribuicaoConfiguravel,
} from './enums'

/**
 * Id de objeto na Meta (`form_id`, `page_id`, `leadgen_id`).
 *
 * A Graph API usa números longos, mas o tipo é string por contrato — id numérico
 * de 17 dígitos não sobrevive a `Number` em JS. O regex barra o que não é
 * identificador para que um valor com espaço ou barra não vá para dentro de uma
 * URL da Graph API montada por concatenação.
 */
export const zIdExternoMeta = z
  .string({ required_error: 'Informe o identificador do formulário na Meta.' })
  .trim()
  .min(1, 'Informe o identificador do formulário na Meta.')
  .max(120, 'Identificador externo muito longo.')
  .regex(/^[A-Za-z0-9._:-]+$/, 'O identificador externo aceita letras, números, . _ : e -.')

// ---------------------------------------------------------------------------
// GET /integracoes/eventos
// ---------------------------------------------------------------------------

export const schemaFiltrosEventos = z
  .object({
    limite: schemaLimite,
    cursor: schemaCursor,
    origem: zOrigemEvento.optional(),
    status: schemaListaDeEnum(STATUS_PROCESSAMENTO, 'Status de processamento'),
    formularioId: zIdCatalogo.optional(),
    /** Busca exata pelo id do evento na origem — é como o suporte rastreia um lead. */
    eventoExternoId: z.string().trim().max(120).optional(),
    /** Dia civil de America/Sao_Paulo (RN-55), aplicado sobre `recebidoEm`. */
    de: zDataFiltro.optional(),
    ate: zDataFiltro.optional(),
    /** Só os que a fila ainda vai tentar de novo, ou já desistiu de tentar. */
    apenasPendentes: z
      .enum(['true', 'false'])
      .optional()
      .transform((valor) => valor === 'true'),
  })
  .strict()

export type FiltrosEventos = z.infer<typeof schemaFiltrosEventos>

// ---------------------------------------------------------------------------
// Formulários — POST/PATCH /integracoes/meta/formularios
// ---------------------------------------------------------------------------

const camposFormulario = {
  titulo: z
    .string({ required_error: 'Informe o título do formulário.' })
    .trim()
    .min(2, 'Informe o título do formulário.')
    .max(160, 'Título muito longo.'),
  formularioExternoId: zIdExternoMeta,
  paginaExternaId: zIdExternoMeta.nullish(),
  unidadeId: zIdCatalogo,
  equipeId: zIdCatalogo,
  produtoAssociadoId: zIdCatalogo.nullish(),
  /** Ponto de conversão do lead que entrar por este formulário (TELA-15). */
  pontoConversaoId: zIdCatalogo.nullish(),
  responsavelPadraoUuid: zUuid.nullish(),
  /**
   * C3: `AUTOMATICA` (rodízio) **não** é aceito na entrada. Ver
   * `zTipoDistribuicaoConfiguravel` e
   * `docs/architecture/03-distribution-decision.md §3.3`.
   */
  distribuicao: zTipoDistribuicaoConfiguravel,
  /** Guarda o nome do formulário na timeline do lead (comportamento da TELA-21). */
  salvarNomeFormulario: z.boolean(),
  ativo: z.boolean(),
  tagIds: z.array(zIdCatalogo).max(20, 'No máximo 20 tags por formulário.'),
}

/**
 * `RESPONSAVEL_FIXO` sem responsável é configuração que só falha quando o
 * primeiro lead chega — de madrugada, sem ninguém para ver. Recusar no cadastro é
 * o que transforma um lead sem dono em um `422` na tela de quem configurou.
 */
const exigirResponsavelQuandoFixo = (
  valor: { distribuicao?: string; responsavelPadraoUuid?: string | null },
  contexto: z.RefinementCtx,
): void => {
  if (valor.distribuicao === 'RESPONSAVEL_FIXO' && !valor.responsavelPadraoUuid) {
    contexto.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['responsavelPadraoUuid'],
      message: 'Escolha o responsável fixo deste formulário.',
    })
  }
}

export const schemaCriarFormularioMeta = z
  .object({
    ...camposFormulario,
    distribuicao: zTipoDistribuicaoConfiguravel.default(TIPO_DISTRIBUICAO_PADRAO),
    salvarNomeFormulario: z.boolean().default(false),
    ativo: z.boolean().default(true),
    tagIds: camposFormulario.tagIds.default([]),
  })
  .strict()
  .superRefine(exigirResponsavelQuandoFixo)

export type EntradaCriarFormularioMeta = z.infer<typeof schemaCriarFormularioMeta>

/**
 * `PATCH` parcial. `formularioExternoId` **não** é editável: trocá-lo em um
 * formulário que já recebeu eventos reescreveria a origem do histórico — os
 * eventos passados continuariam apontando para este registro, agora descrevendo
 * outro formulário. Formulário novo é registro novo.
 */
export const schemaAtualizarFormularioMeta = z
  .object({
    titulo: camposFormulario.titulo.optional(),
    paginaExternaId: camposFormulario.paginaExternaId,
    unidadeId: camposFormulario.unidadeId.optional(),
    equipeId: camposFormulario.equipeId.optional(),
    produtoAssociadoId: camposFormulario.produtoAssociadoId,
    pontoConversaoId: camposFormulario.pontoConversaoId,
    responsavelPadraoUuid: camposFormulario.responsavelPadraoUuid,
    distribuicao: zTipoDistribuicaoConfiguravel.optional(),
    salvarNomeFormulario: z.boolean().optional(),
    ativo: z.boolean().optional(),
    tagIds: camposFormulario.tagIds.optional(),
  })
  .strict()
  .refine(
    (valor) => Object.keys(valor).length > 0,
    'Informe ao menos um campo para alterar.',
  )
  .superRefine(exigirResponsavelQuandoFixo)

export type EntradaAtualizarFormularioMeta = z.infer<typeof schemaAtualizarFormularioMeta>

export const schemaFiltrosFormularios = z
  .object({
    ativo: z
      .enum(['true', 'false'])
      .optional()
      .transform((valor) => (valor === undefined ? undefined : valor === 'true')),
    unidadeId: zIdCatalogo.optional(),
    equipeId: zIdCatalogo.optional(),
    busca: z.string().trim().min(1).max(120).optional(),
  })
  .strict()

export type FiltrosFormularios = z.infer<typeof schemaFiltrosFormularios>

// ---------------------------------------------------------------------------
// POST /integracoes/meta/formularios/{id}/sincronizar
// ---------------------------------------------------------------------------

export const schemaSincronizarFormulario = z
  .object({
    /** Dia civil de São Paulo. Ausente, usa a última sincronização com sobreposição. */
    desde: zDataFiltro.optional(),
    limite: z.coerce.number().int().min(1).max(500).default(100),
  })
  .strict()

export type EntradaSincronizarFormulario = z.infer<typeof schemaSincronizarFormulario>

// ---------------------------------------------------------------------------
// POST /integracoes/meta/formularios/{id}/testar — TELA-22
// ---------------------------------------------------------------------------

/**
 * Simulação de um envio do formulário, sem depender da Meta (item 9 do escopo).
 *
 * `apenasMapear` é o padrão **falso** porque a TELA-22 do legado é um teste de
 * ponta a ponta: quem configura o formulário quer ver o lead aparecer no painel.
 * Com `true`, a rota devolve só o relatório de mapeamento — útil para conferir o
 * casamento de campos de um formulário em produção sem sujar a base.
 */
export const schemaTestarFormulario = z
  .object({
    nome: z.string().trim().min(2).max(160).default('Lead de Teste Meta'),
    email: z.string().trim().toLowerCase().email('Informe um e-mail válido.').max(320).nullish(),
    telefone: z.string().trim().min(8).max(25).default('+5562999990000'),
    cidade: z.string().trim().max(120).nullish(),
    /** Campos livres do formulário, preservados em `payload_externo` (RN-64). */
    camposExtras: z.record(z.string().max(120), z.string().max(2000)).default({}),
    apenasMapear: z.boolean().default(false),
  })
  .strict()

export type EntradaTestarFormulario = z.infer<typeof schemaTestarFormulario>

// ---------------------------------------------------------------------------
// Webhook — handshake de verificação (GET)
// ---------------------------------------------------------------------------

/**
 * Query do handshake da Meta. Os nomes com ponto são da Meta, não nossos.
 *
 * Sem `.strict()`: a Meta acrescenta parâmetros de tempos em tempos, e recusar o
 * handshake por causa de um parâmetro novo derrubaria a assinatura do webhook.
 */
export const schemaHandshakeWebhook = z.object({
  'hub.mode': z.string().trim().max(40).optional(),
  'hub.verify_token': z.string().trim().max(400).optional(),
  'hub.challenge': z.string().trim().max(400).optional(),
})

export type EntradaHandshakeWebhook = z.infer<typeof schemaHandshakeWebhook>
